import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import { MaterialPicker } from '../pickers/MaterialPicker';
import VendorPicker from '../pickers/VendorPicker';
import { inrCompact, fmtDMY } from '../../lib/format';
import { ChargesTable, cleanCharges } from './ChargesTable';
import { MprLinePicker } from './MprLinePicker';

/* ─── Shared helpers ─────────────────────────────────────────────────────── */
const blankItem = () => ({ _key: Math.random(), material_id: '', material_name: '', uom: '', qty: '', rate: '', hsn_code: '', gst_rate: 18, notes: '' });

const StepIndicator = ({ steps, current }) => (
  <div className="flex items-center gap-0 mb-6">
    {steps.map((s, i) => (
      <React.Fragment key={i}>
        <div className={`flex items-center gap-2 ${i <= current ? 'text-[#3b4872]' : 'text-slate-400'}`}>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-display font-bold border-2 transition-colors ${i < current ? 'bg-[#3b4872] border-[#3b4872] text-white' : i === current ? 'border-[#3b4872] text-[#3b4872] bg-white' : 'border-slate-300 text-slate-400 bg-white'}`}>
            {i < current ? <CheckCircle2 size={12} /> : i + 1}
          </div>
          <span className="text-[10px] font-display font-bold uppercase tracking-wider whitespace-nowrap hidden sm:block">{s}</span>
        </div>
        {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i < current ? 'bg-[#3b4872]' : 'bg-slate-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
    {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
  </label>
);

const FieldInput = (props) => (
  <input {...props} className={`w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none ${props.className || ''}`} />
);

const FieldSelect = ({ children, ...props }) => (
  <select {...props} className={`w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:border-[#3b4872] focus:outline-none ${props.className || ''}`}>
    {children}
  </select>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MPR WIZARD
   Steps: Project & Details → Add Materials → Review
═══════════════════════════════════════════════════════════════════════════ */
function MPRWizard({ onClose, onSaved, defaultProjectId }) {
  const [step, setStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [pmsCaps, setPmsCaps] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState({
    project_id: defaultProjectId || '', priority: 'Normal', required_by: '',
    plan_frequency: 'Monthly', source_basis: 'BOQ', justification: '',
  });
  const [items, setItems] = useState([blankItem()]);

  const MPR_FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'Entire Project'];
  const SOURCE_BASES = ['WBS', 'BOQ', 'DPR'];

  useEffect(() => {
    apiGet('/projects').then(d => setProjects(Array.isArray(d) ? d : d?.items || [])).catch(() => {});
  }, []);

  const loadCaps = useCallback(async (pid) => {
    if (!pid) { setPmsCaps({}); return; }
    try {
      const data = await apiGet(`/project-planning/pms-cap?project_id=${pid}`);
      if (data.has_pms) {
        const map = {};
        (data.caps || []).forEach(c => { if (c.material_id) map[c.material_id] = c; });
        setPmsCaps(map);
      } else { setPmsCaps({}); }
    } catch { setPmsCaps({}); }
  }, []);

  useEffect(() => { loadCaps(meta.project_id); }, [meta.project_id, loadCaps]);

  const updateItem = (idx, k, v) => setItems(is => is.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const pickMaterial = (idx, mat) => setItems(is => is.map((it, i) => i === idx
    ? { ...it, material_id: mat?.id || '', material_name: mat?.name || '', uom: mat?.uom || '' }
    : it));
  const addItem = () => setItems(is => [...is, blankItem()]);
  const removeItem = (idx) => setItems(is => is.filter((_, i) => i !== idx));

  const validItems = items.filter(it => it.material_id && parseFloat(it.qty) > 0);

  const canNext = step === 0
    ? !!meta.project_id && !!meta.plan_frequency && !!meta.source_basis
    : step === 1
      ? validItems.length > 0
      : true;

  const submit = async (asDraft) => {
    if (!validItems.length) { toast.error('Add at least one item with qty > 0'); return; }
    setSubmitting(true);
    try {
      const proj = projects.find(p => p.id === meta.project_id);
      await apiPost('/mpr', {
        ...meta,
        project: proj?.name || '',
        project_name: proj?.name || '',
        line_items: validItems.map(it => ({ material_id: it.material_id, qty: Number(it.qty), uom: it.uom, notes: it.notes })),
        submit_for_approval: !asDraft,
      });
      toast.success(asDraft ? 'MPR saved as Draft' : 'MPR submitted to PM for approval');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create MPR');
    } finally { setSubmitting(false); }
  };

  const selProject = projects.find(p => p.id === meta.project_id);

  return (
    <>
      <StepIndicator steps={['Project & Details', 'Add Materials', 'Review']} current={step} />

      {/* Step 0 — Project & Details */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FieldLabel required>Project</FieldLabel>
              <FieldSelect value={meta.project_id} onChange={e => setMeta(m => ({ ...m, project_id: e.target.value }))} data-testid="mpr-wiz-project">
                <option value="">Select project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel required>Plan Frequency</FieldLabel>
              <FieldSelect value={meta.plan_frequency} onChange={e => setMeta(m => ({ ...m, plan_frequency: e.target.value }))} data-testid="mpr-wiz-frequency">
                {MPR_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel required>Source Basis</FieldLabel>
              <FieldSelect value={meta.source_basis} onChange={e => setMeta(m => ({ ...m, source_basis: e.target.value }))} data-testid="mpr-wiz-source">
                {SOURCE_BASES.map(s => <option key={s}>{s}</option>)}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel>Priority</FieldLabel>
              <FieldSelect value={meta.priority} onChange={e => setMeta(m => ({ ...m, priority: e.target.value }))} data-testid="mpr-wiz-priority">
                {['Normal', 'Urgent', 'Critical'].map(p => <option key={p}>{p}</option>)}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel>Required By</FieldLabel>
              <FieldInput type="date" value={meta.required_by} onChange={e => setMeta(m => ({ ...m, required_by: e.target.value }))} data-testid="mpr-wiz-required-by" />
            </div>
            <div className="col-span-2">
              <FieldLabel>Justification / Remarks</FieldLabel>
              <textarea value={meta.justification} onChange={e => setMeta(m => ({ ...m, justification: e.target.value }))}
                rows={2} data-testid="mpr-wiz-justification"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none resize-none" />
            </div>
          </div>
        </div>
      )}

      {/* Step 1 — Add Materials */}
      {step === 1 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">Add materials you need for <span className="font-semibold text-[#3b4872]">{selProject?.name}</span>. PMS cap shown where available.</p>
            <button onClick={addItem} data-testid="mpr-wiz-add-item"
              className="flex items-center gap-1 text-xs font-display font-semibold text-[#3b4872] border border-[#3b4872] px-2.5 py-1 rounded-sm hover:bg-indigo-50">
              <Plus size={11} /> Add Item
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {items.map((it, idx) => {
              const cap = pmsCaps[it.material_id];
              const reqQty = parseFloat(it.qty) || 0;
              const capExceeded = cap && cap.cap > 0 && reqQty > cap.remaining;
              return (
                <div key={it._key} className={`p-3 rounded-sm border ${capExceeded ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`mpr-wiz-item-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-5">
                      <MaterialPicker value={it.material_id} displayName={it.material_name} onSelect={mat => pickMaterial(idx, mat)} testid={`mpr-wiz-mat-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <FieldInput type="number" min="0" step="any" placeholder="Qty"
                        value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)}
                        data-testid={`mpr-wiz-qty-${idx}`}
                        className={capExceeded ? 'border-rose-400' : ''} />
                    </div>
                    <div className="col-span-2">
                      <FieldInput placeholder="UoM" value={it.uom} onChange={e => updateItem(idx, 'uom', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <FieldInput placeholder="Notes" value={it.notes} onChange={e => updateItem(idx, 'notes', e.target.value)} />
                    </div>
                    <div className="col-span-1 flex items-center justify-end pt-1">
                      <button onClick={() => removeItem(idx)} disabled={items.length === 1}
                        className="p-1 text-rose-400 hover:text-rose-600 disabled:opacity-30 rounded-sm">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {cap && (
                    <div className={`mt-1.5 text-[10px] font-display font-semibold flex items-center gap-1 ${capExceeded ? 'text-rose-600' : 'text-emerald-700'}`} data-testid={`mpr-wiz-cap-${idx}`}>
                      {capExceeded ? <AlertTriangle size={9} /> : '✓'}
                      PMS Cap: {cap.cap} {cap.uom} · Committed: {cap.committed} · Remaining: <b>{cap.remaining}</b>
                      {capExceeded && ' — EXCEEDS CAP'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2 — Review */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-xs text-slate-500">Project</span><span className="font-semibold">{selProject?.name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-xs text-slate-500">Frequency</span><span>{meta.plan_frequency}</span></div>
            <div className="flex justify-between"><span className="text-xs text-slate-500">Source</span><span>{meta.source_basis}</span></div>
            <div className="flex justify-between"><span className="text-xs text-slate-500">Priority</span><span>{meta.priority}</span></div>
            {meta.required_by && <div className="flex justify-between"><span className="text-xs text-slate-500">Required By</span><span>{meta.required_by}</span></div>}
          </div>
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">{validItems.length} Line Item{validItems.length !== 1 ? 's' : ''}</div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-200"><th className="text-left pb-1 font-display font-bold text-slate-500">Material</th><th className="text-right pb-1 font-display font-bold text-slate-500">Qty</th><th className="text-left pb-1 font-display font-bold text-slate-500 pl-2">UoM</th></tr></thead>
              <tbody>
                {validItems.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5 font-medium">{it.material_name}</td>
                    <td className="py-1.5 text-right tabular-nums font-bold">{it.qty}</td>
                    <td className="py-1.5 text-slate-500 pl-2">{it.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.justification && <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-2">{meta.justification}</div>}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
        <button onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
          className="flex items-center gap-1 px-4 py-2 text-sm border border-slate-300 rounded-sm text-slate-600 hover:border-slate-400">
          <ChevronLeft size={14} /> {step === 0 ? 'Cancel' : 'Back'}
        </button>
        {step < 2 ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext}
            data-testid="mpr-wiz-next"
            className="flex items-center gap-1 px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-40">
            Next <ChevronRight size={14} />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => submit(true)} disabled={submitting}
              data-testid="mpr-wiz-draft" className="px-4 py-2 text-sm border border-slate-300 rounded-sm font-display font-semibold hover:border-slate-400 disabled:opacity-40">
              Save as Draft
            </button>
            <button onClick={() => submit(false)} disabled={submitting}
              data-testid="mpr-wiz-submit"
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-40">
              {submitting ? <><Loader2 size={13} className="animate-spin" /> Submitting…</> : 'Submit to PM'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   PO WIZARD
   Steps: Vendor & Project → Line Items → Terms & Review
═══════════════════════════════════════════════════════════════════════════ */
function POWizard({ onClose, onSaved, defaultProjectId }) {
  const [step, setStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState({
    project_id: defaultProjectId || '', vendor_id: '', vendor_name: '',
    po_date: new Date().toISOString().slice(0, 10),
    delivery_date: '', delivery_address: '',
    bypass_reason: '', bypass_approved_by: '', terms: '', notes: '',
    // Item 5 (Fixes_Combined): the wizard gets the same MPR / T&C / charges
    // fields as the Manual PO modal, and drops the bypass fields entirely for
    // an MPR-based (routine) PO.
    mpr_id: '', tc_set_id: '', extra_clause: '',
    charges: [],
  });
  const [approvedMprs, setApprovedMprs] = useState([]);
  const [tcSets, setTcSets] = useState([]);
  const [lineItems, setLineItems] = useState([blankItem()]);
  const today = new Date();
  const minDateStr = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate()).toISOString().slice(0, 10);
  const maxDateStr = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate()).toISOString().slice(0, 10);

  useEffect(() => {
    apiGet('/projects').then(d => setProjects(Array.isArray(d) ? d : d?.items || [])).catch(() => {});
  }, []);

  const selProject = projects.find(p => p.id === meta.project_id);

  // R7 (Fixes round 4): pre-fill delivery_address from the project, same as
  // the Award modal's ship_to prefill — projects only carry address /
  // site_address / location, never delivery_address itself.
  // V1 (Fixes round 9): refill (not just fill-if-empty) whenever the project
  // actually CHANGES — switching project A → B must not leave A's site behind.
  const prevProjectIdRef = useRef(meta.project_id);
  useEffect(() => {
    if (meta.project_id && meta.project_id !== prevProjectIdRef.current) {
      const addr = selProject?.site_address || selProject?.address || selProject?.location || '';
      if (addr) setMeta(m => ({ ...m, delivery_address: addr }));
    }
    prevProjectIdRef.current = meta.project_id;
  }, [meta.project_id, selProject]);

  const updateItem = (idx, k, v) => setLineItems(is => is.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const selectMaterial = (idx, mat) => setLineItems(is => is.map((it, i) => i === idx
    ? { ...it, material_id: mat?.id || '', material_name: mat?.name || '', uom: it.uom || mat?.uom || '', hsn_code: mat?.hsn_code || '', gst_rate: mat?.gst_rate ?? 18 }
    : it));
  const addItem = () => setLineItems(is => [...is, blankItem()]);
  const removeItem = (idx) => setLineItems(is => is.filter((_, i) => i !== idx));

  const validItems = lineItems.filter(it => it.material_id && parseFloat(it.qty) > 0 && parseFloat(it.rate) >= 0);
  const subtotal = validItems.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0), 0);
  const totalGST = validItems.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0) * ((parseFloat(it.gst_rate) || 0) / 100), 0);
  const grandTotal = subtotal + totalGST;

  // A1 — bypass_reason was missing from this check, letting step 0 advance
  // with an approver name but no documented reason for skipping RFQ/CS.
  useEffect(() => {
    apiGet('/projects').then(d => setProjects(Array.isArray(d) ? d : d?.items || [])).catch(() => {});
    apiGet('/settings/tc-library').then(d => setTcSets(d?.sets || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!meta.project_id) { setApprovedMprs([]); return; }
    apiGet('/mpr', { status: 'PM Approved', project_id: meta.project_id, limit: 100 })
      .then(d => setApprovedMprs((d?.items || d || []).filter(m => m.project_id === meta.project_id)))
      .catch(() => setApprovedMprs([]));
  }, [meta.project_id]);

  const addMprLines = async (lines) => {
    const mats = await Promise.all(lines.map(li => apiGet(`/material-master/${li.material_id}`).catch(() => null)));
    setLineItems(prev => [...prev.filter(it => it.material_id), ...lines.map((li, i) => ({
      ...blankItem(), material_id: li.material_id, material_name: li.material_name || '', material_code: mats[i]?.material_id || '',
      qty: li.qty, uom: li.unit || mats[i]?.uom || 'Nos', hsn_code: mats[i]?.hsn_code || '', gst_rate: mats[i]?.gst_rate ?? 18,
      mpr_id: li.mpr_id, mpr_line_id: li.mpr_line_id, project_id: li.project_id, project_name: li.project_name,
    }))]);
    if (lines[0]) setMeta(m => ({ ...m, mpr_id: m.mpr_id || lines[0].mpr_id }));
  };
  const selectMpr = async (mprId) => {
    setMeta(m => ({ ...m, mpr_id: mprId }));
    const mp = approvedMprs.find(x => x.id === mprId);
    // R6 (Fixes round 4): one PO per approved MPR line — don't re-offer a
    // line that's already on a (non-cancelled) PO.
    const covered = await apiGet(`/mpr/${mprId}/po-covered-materials`).then(d => new Set(d?.covered_material_ids || [])).catch(() => new Set());
    const lines = (mp?.line_items || []).filter(li => li.material_id && !covered.has(li.material_id));
    if (lines.length) {
      // Followup round 2, P3: HSN lives on the Material Master, not the MPR line.
      const mats = await Promise.all(lines.map(li => apiGet(`/material-master/${li.material_id}`).catch(() => null)));
      setLineItems(lines.map((li, i) => ({
        ...blankItem(), material_id: li.material_id, material_name: li.material_name || '',
        material_code: li.material_code || '', qty: li.qty || 1, uom: li.uom || 'Nos',
        hsn_code: li.hsn_code || mats[i]?.hsn_code || '',
        gst_rate: li.gst_rate != null ? li.gst_rate : (mats[i]?.gst_rate ?? 18),
      })));
    } else if (mp?.material_id && !covered.has(mp.material_id)) {
      const mat = await apiGet(`/material-master/${mp.material_id}`).catch(() => null);
      setLineItems([{ ...blankItem(), material_id: mp.material_id, material_name: mp.material_name || mp.item || '',
        material_code: mp.material_code || '', qty: mp.qty || mp.quantity || 1, uom: mp.uom || mp.unit || 'Nos',
        hsn_code: mat?.hsn_code || '', gst_rate: mat?.gst_rate ?? 18 }]);
    }
  };
  const bypassRequired = !meta.mpr_id;

  const canNext = step === 0
    ? !!meta.vendor_id && !!meta.project_id && (!bypassRequired || (!!meta.bypass_approved_by && !!meta.bypass_reason))
    : step === 1
      ? validItems.length > 0
      : true;

  const submit = async () => {
    if (!validItems.length) { toast.error('Add at least one line item'); return; }
    // A2 — same PO-date sanity as the backend now enforces.
    if (meta.delivery_date && meta.po_date && meta.delivery_date < meta.po_date) {
      toast.error('Expected Delivery cannot be before the PO Date.'); return;
    }
    if (meta.po_date && (meta.po_date < minDateStr || meta.po_date > maxDateStr)) {
      toast.error('PO Date must be within 5 years of today.'); return;
    }
    if (meta.delivery_date && (meta.delivery_date < minDateStr || meta.delivery_date > maxDateStr)) {
      toast.error('Expected Delivery must be within 5 years of today.'); return;
    }
    setSubmitting(true);
    try {
      const proj = selProject;
      const payload = {
        vendor_id: meta.vendor_id,
        vendor: meta.vendor_name || '',
        project_id: meta.project_id,
        project: proj?.name || proj?.code || '',
        po_date: meta.po_date,
        delivery_date: meta.delivery_date,
        delivery_address: meta.delivery_address,
        items: validItems.map(it => ({
          material_id: it.material_id,
          description: it.material_name,
          material_code: it.material_code || '',
          qty: parseFloat(it.qty),
          unit: it.uom || 'Nos',
          rate: parseFloat(it.rate) || 0,
          hsn_code: it.hsn_code || '',
          gst_rate: parseFloat(it.gst_rate) || 18,
        })),
        terms: meta.terms,
        notes: meta.notes,
        value: Math.round(subtotal),
        grand_total: Math.round(grandTotal),
        status: 'Pending',
        source: 'Manual',
        is_direct_po: true,
        mpr_id: meta.mpr_id || undefined,
        tc_set_id: meta.tc_set_id || undefined,
        extra_clause: meta.extra_clause || undefined,
        charges: cleanCharges(meta.charges),
        bypass_reason: meta.mpr_id ? undefined : (meta.bypass_reason || 'Wizard-created direct PO'),
        bypass_approved_by: meta.mpr_id ? undefined : (meta.bypass_approved_by || ''),
      };
      await apiPost('/po', payload);
      toast.success(`PO created · ${inrCompact(grandTotal)}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create PO');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <StepIndicator steps={['Vendor & Project', 'Line Items', 'Terms & Review']} current={step} />

      {/* Step 0 — Vendor & Project */}
      {step === 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FieldLabel required>Vendor</FieldLabel>
            <VendorPicker
              value={meta.vendor_id}
              displayName={meta.vendor_name}
              onSelect={(v) => setMeta(m => ({ ...m, vendor_id: v?.id || '', vendor_name: v?.name || '' }))}
              placeholder="Pick an empanelled vendor…"
              testid="po-wiz-vendor-picker"
            />
          </div>
          <div className="col-span-2">
            <FieldLabel required>Project</FieldLabel>
            <FieldSelect value={meta.project_id} onChange={e => setMeta(m => ({ ...m, project_id: e.target.value }))} data-testid="po-wiz-project">
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
            </FieldSelect>
          </div>
          <div>
            <FieldLabel>PO Date</FieldLabel>
            <FieldInput type="date" value={meta.po_date} onChange={e => setMeta(m => ({ ...m, po_date: e.target.value }))} data-testid="po-wiz-date" />
          </div>
          <div>
            <FieldLabel>Delivery Date</FieldLabel>
            <FieldInput type="date" value={meta.delivery_date} onChange={e => setMeta(m => ({ ...m, delivery_date: e.target.value }))} data-testid="po-wiz-delivery-date" />
          </div>
          <div className="col-span-2">
            <FieldLabel>Delivery Address</FieldLabel>
            <FieldInput value={meta.delivery_address} onChange={e => setMeta(m => ({ ...m, delivery_address: e.target.value }))} placeholder="Site address or warehouse" data-testid="po-wiz-delivery-address" />
          </div>
          <div className="col-span-2">
            <FieldLabel>Approved MPR <span className="text-[9px] text-slate-400 normal-case font-normal tracking-normal">(routine purchase — no bypass needed)</span></FieldLabel>
            <FieldSelect value={meta.mpr_id} onChange={e => selectMpr(e.target.value)} data-testid="po-wiz-mpr">
              <option value="">{meta.project_id ? '— None (standalone / bypass) —' : '— Select a project first —'}</option>
              {approvedMprs.map(m => { const n = (m.line_items || m.items || []).length || 1; const req = m.required_by || m.required_date; return <option key={m.id} value={m.id}>{`${m.mpr_no} · ${fmtDMY(m.created_at)} · ${n} item${n === 1 ? '' : 's'}${req ? ` · required by ${fmtDMY(req)}` : ''}`}</option>; })}
            </FieldSelect>
            {selProject && !selProject.site_state && (
              <div className="text-[11px] text-amber-700 mt-1" data-testid="po-wiz-site-state-warning">Project site state is missing — GST split may be wrong. Update the project.</div>
            )}
          </div>
          <div>
            <FieldLabel>Standard T&amp;C Set</FieldLabel>
            <FieldSelect value={meta.tc_set_id} onChange={e => setMeta(m => ({ ...m, tc_set_id: e.target.value }))} data-testid="po-wiz-tc-set">
              <option value="">— Default set —</option>
              {tcSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </FieldSelect>
          </div>
          <div>
            <FieldLabel>Extra Clause</FieldLabel>
            <FieldInput value={meta.extra_clause} onChange={e => setMeta(m => ({ ...m, extra_clause: e.target.value }))} placeholder="One additional clause for this PO only" data-testid="po-wiz-extra-clause" />
          </div>
          <div className="col-span-2">
            <MprLinePicker projects={projects || []} onAdd={addMprLines} testid="po-wiz-mpr-picker" />
          </div>
          <div className="col-span-2">
            <ChargesTable charges={meta.charges || []} onChange={c => setMeta(m => ({ ...m, charges: c }))} items={lineItems} testid="po-wiz-charges" />
          </div>
          {bypassRequired && (<>
          <div className="col-span-2">
            <FieldLabel>Bypass Reason <span className="text-[9px] text-slate-400 normal-case font-normal tracking-normal">(required for direct PO without RFQ)</span></FieldLabel>
            <FieldInput value={meta.bypass_reason} onChange={e => setMeta(m => ({ ...m, bypass_reason: e.target.value }))} placeholder="e.g. Urgency, approved by MD, rate contract" data-testid="po-wiz-bypass-reason" />
          </div>
          <div className="col-span-2">
            <FieldLabel required>Bypass Approved By <span className="text-[9px] text-slate-400 normal-case font-normal tracking-normal">(name / designation of approver)</span></FieldLabel>
            <FieldInput value={meta.bypass_approved_by} onChange={e => setMeta(m => ({ ...m, bypass_approved_by: e.target.value }))} placeholder="e.g. MD Omkar Shah" data-testid="po-wiz-bypass-approved-by" />
          </div>
          </>)}
        </div>
      )}

      {/* Step 1 — Line Items */}
      {step === 1 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">Add materials for <span className="font-semibold text-[#3b4872]">{meta.vendor_name}</span> · {selProject?.name}</p>
            <button onClick={addItem} data-testid="po-wiz-add-item"
              className="flex items-center gap-1 text-xs font-display font-semibold text-[#3b4872] border border-[#3b4872] px-2.5 py-1 rounded-sm hover:bg-indigo-50">
              <Plus size={11} /> Add Line
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {lineItems.map((it, idx) => (
              <div key={it._key} className="border border-slate-200 rounded-sm p-2.5 bg-slate-50" data-testid={`po-wiz-item-${idx}`}>
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-4">
                    <MaterialPicker value={it.material_id} displayName={it.material_name} onSelect={mat => selectMaterial(idx, mat)} testid={`po-wiz-mat-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <FieldInput type="number" min="0" step="any" placeholder="Qty" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} data-testid={`po-wiz-qty-${idx}`} />
                  </div>
                  <div className="col-span-1">
                    <FieldInput placeholder="UoM" value={it.uom} onChange={e => updateItem(idx, 'uom', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <FieldInput type="number" min="0" step="any" placeholder="Rate ₹" value={it.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} data-testid={`po-wiz-rate-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <FieldInput type="number" min="0" max="28" step="0.01" placeholder="GST %" value={it.gst_rate} onChange={e => updateItem(idx, 'gst_rate', e.target.value)} data-testid={`po-wiz-gst-${idx}`} />
                  </div>
                  <div className="col-span-1 flex items-center justify-end pt-1">
                    <button onClick={() => removeItem(idx)} disabled={lineItems.length === 1} className="p-1 text-rose-400 hover:text-rose-600 disabled:opacity-30">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {it.material_id && parseFloat(it.qty) > 0 && parseFloat(it.rate) > 0 && (
                  <div className="mt-1.5 text-right text-[10px] text-slate-500 font-display font-semibold">
                    Subtotal: ₹{((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0)).toLocaleString('en-IN')} + GST {it.gst_rate}%
                  </div>
                )}
              </div>
            ))}
          </div>
          {validItems.length > 0 && (
            <div className="mt-3 flex justify-end gap-6 text-sm border-t border-slate-200 pt-2">
              <span className="text-slate-500">Subtotal: <b>₹{subtotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</b></span>
              <span className="text-slate-500">GST: <b>₹{totalGST.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</b></span>
              <span className="text-[#3b4872] font-bold" data-testid="po-wiz-grand-total">Grand Total: ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — Terms & Review */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FieldLabel>Payment Terms</FieldLabel>
              <FieldInput value={meta.terms} onChange={e => setMeta(m => ({ ...m, terms: e.target.value }))} placeholder="e.g. 30 days credit, advance 30%" data-testid="po-wiz-terms" />
            </div>
            <div className="col-span-2">
              <FieldLabel>Notes</FieldLabel>
              <textarea value={meta.notes} onChange={e => setMeta(m => ({ ...m, notes: e.target.value }))}
                rows={2} data-testid="po-wiz-notes"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none resize-none"
                placeholder="Any special instructions…" />
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-3">Order Summary</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Vendor</span><span className="font-semibold">{meta.vendor_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Project</span><span>{selProject?.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Items</span><span>{validItems.length} line item{validItems.length !== 1 ? 's' : ''}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>₹{subtotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">GST</span><span>₹{totalGST.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
              <div className="flex justify-between border-t border-slate-300 pt-1.5 mt-1.5">
                <span className="font-bold text-[#3b4872]">Grand Total</span>
                <span className="font-bold text-[#3b4872] text-base" data-testid="po-wiz-review-total">₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2.5 text-xs text-amber-900">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            This will create a Direct PO (bypasses RFQ). The PO will be in Pending status — an approver must approve it before it becomes active.
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
        <button onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
          className="flex items-center gap-1 px-4 py-2 text-sm border border-slate-300 rounded-sm text-slate-600 hover:border-slate-400">
          <ChevronLeft size={14} /> {step === 0 ? 'Cancel' : 'Back'}
        </button>
        {step < 2 ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext}
            data-testid="po-wiz-next"
            className="flex items-center gap-1 px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-40">
            Next <ChevronRight size={14} />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting}
            data-testid="po-wiz-create"
            className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-40">
            {submitting ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : 'Create PO'}
          </button>
        )}
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT — Wizard shell with type picker
═══════════════════════════════════════════════════════════════════════════ */
export default function ProcurementWizard({ onClose, onSaved, defaultType, defaultProjectId }) {
  const [type, setType] = useState(defaultType || null); // 'mpr' | 'po'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="procurement-wizard">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">
              {type === 'mpr' ? 'Material Requisition' : type === 'po' ? 'Purchase Order' : 'Procurement'}
            </div>
            <h3 className="font-display font-bold text-xl mt-0.5">
              {type === 'mpr' ? 'New MPR Wizard' : type === 'po' ? 'New PO Wizard' : 'Create New…'}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid="procurement-wizard-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {/* Type picker */}
          {!type && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-4">What would you like to create?</p>
              <button onClick={() => setType('mpr')} data-testid="wiz-pick-mpr"
                className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 rounded-sm hover:border-[#3b4872] hover:bg-indigo-50 text-left transition-colors group">
                <div className="w-10 h-10 rounded-full bg-indigo-50 group-hover:bg-[#3b4872]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#3b4872]">M</span>
                </div>
                <div>
                  <div className="font-display font-bold text-sm text-slate-800">Material Purchase Requisition (MPR)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Request materials for a project. PM approves → Procurement raises RFQ or Direct PO.</div>
                </div>
                <ChevronRight size={18} className="ml-auto text-slate-400 group-hover:text-[#3b4872]" />
              </button>
              <button onClick={() => setType('po')} data-testid="wiz-pick-po"
                className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 rounded-sm hover:border-[#3b4872] hover:bg-indigo-50 text-left transition-colors group">
                <div className="w-10 h-10 rounded-full bg-amber-50 group-hover:bg-amber-100 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-amber-700">P</span>
                </div>
                <div>
                  <div className="font-display font-bold text-sm text-slate-800">Direct Purchase Order (PO)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Create a PO directly with a vendor, bypassing the RFQ process. Requires bypass justification.</div>
                </div>
                <ChevronRight size={18} className="ml-auto text-slate-400 group-hover:text-[#3b4872]" />
              </button>
            </div>
          )}

          {type === 'mpr' && <MPRWizard onClose={onClose} onSaved={onSaved} defaultProjectId={defaultProjectId} />}
          {type === 'po' && <POWizard onClose={onClose} onSaved={onSaved} defaultProjectId={defaultProjectId} />}
        </div>
      </div>
    </div>
  );
}
